import { useState, useEffect } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  terminologyService,
  type TerminologyRule,
  type TerminologyRuleCreateData,
  type TerminologyStats,
} from '@/services/terminology-service';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
  Label,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { Plus, Edit, Trash2, MoreHorizontal, FileText, Search } from '@/lib/icons';

const SEVERITY_VARIANTS: Record<string, 'destructive' | 'default' | 'secondary'> = {
  critical: 'destructive',
  major: 'default',
  minor: 'secondary',
};

const RULE_TYPE_LABELS: Record<string, string> = {
  trademark: 'Trademark',
  forbidden: 'Forbidden',
  approved_term: 'Approved',
  wrong_correct: 'Wrong/Correct',
};

type RuleForm = {
  rule_type: string;
  severity: string;
  category: string;
  pattern: string;
  pattern_type: string;
  case_sensitive: boolean;
  replacement: string;
  replacement_first_use: string;
  replacement_subsequent: string;
  usage_notes: string;
  is_active: boolean;
  sort_order: number;
};

const emptyForm: RuleForm = {
  rule_type: 'wrong_correct',
  severity: 'minor',
  category: '',
  pattern: '',
  pattern_type: 'exact',
  case_sensitive: false,
  replacement: '',
  replacement_first_use: '',
  replacement_subsequent: '',
  usage_notes: '',
  is_active: true,
  sort_order: 0,
};

export function KaiyaTerminologyPage() {
  const { setShowPageHeader } = usePageContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ruleTypeFilter, setRuleTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingRule, setEditingRule] = useState<TerminologyRule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<RuleForm>(emptyForm);
  const [ruleToDelete, setRuleToDelete] = useState<TerminologyRule | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data, isLoading } = useQuery({
    queryKey: ['terminologyRules', searchQuery, categoryFilter, ruleTypeFilter, severityFilter, showInactive],
    queryFn: () =>
      terminologyService.list({
        ...(searchQuery ? { q: searchQuery } : {}),
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(ruleTypeFilter ? { rule_type: ruleTypeFilter } : {}),
        ...(severityFilter ? { severity: severityFilter } : {}),
        ...(!showInactive ? { active: 'true' } : {}),
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['terminologyStats'],
    queryFn: () => terminologyService.stats(),
  });

  const createMutation = useMutation({
    mutationFn: (data: TerminologyRuleCreateData) => terminologyService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminologyRules'] });
      queryClient.invalidateQueries({ queryKey: ['terminologyStats'] });
      toast.success('Rule created');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create rule'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: TerminologyRuleCreateData }) =>
      terminologyService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminologyRules'] });
      queryClient.invalidateQueries({ queryKey: ['terminologyStats'] });
      toast.success('Rule updated');
      closeForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => terminologyService.destroy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminologyRules'] });
      queryClient.invalidateQueries({ queryKey: ['terminologyStats'] });
      toast.success('Rule deleted');
      setRuleToDelete(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete rule'),
  });

  const rules: TerminologyRule[] =
    extractAttributesList(data);

  const typedStats = stats as TerminologyStats | undefined;

  const openCreate = () => {
    setEditingRule(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (rule: TerminologyRule) => {
    setEditingRule(rule);
    setForm({
      rule_type: rule.rule_type,
      severity: rule.severity,
      category: rule.category || '',
      pattern: rule.pattern,
      pattern_type: rule.pattern_type,
      case_sensitive: rule.case_sensitive,
      replacement: rule.replacement || '',
      replacement_first_use: rule.replacement_first_use || '',
      replacement_subsequent: rule.replacement_subsequent || '',
      usage_notes: rule.usage_notes || '',
      is_active: rule.is_active,
      sort_order: rule.sort_order,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRule(null);
    setForm(emptyForm);
  };

  const handleSave = () => {
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Header
        title="Kaiya Terminology"
        description="Manage terminology rules for Kaiya's response quality"
      />

      <ContentContainer>
        <div className="flex justify-end mb-4">
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Rule
          </Button>
        </div>

        {/* Stats cards */}
        {typedStats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{typedStats.total}</div>
                <div className="text-sm text-muted-foreground">Total Rules</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{typedStats.categories.length}</div>
                <div className="text-sm text-muted-foreground">Categories</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{typedStats.by_severity.critical}</div>
                <div className="text-sm text-muted-foreground">Critical Rules</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              className="pl-9 pr-3 py-2 border rounded-md text-sm w-[240px]"
              placeholder="Search patterns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={categoryFilter || 'all'}
            onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {typedStats?.categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={ruleTypeFilter || 'all'}
            onValueChange={(v) => setRuleTypeFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="trademark">Trademark</SelectItem>
              <SelectItem value="forbidden">Forbidden</SelectItem>
              <SelectItem value="approved_term">Approved</SelectItem>
              <SelectItem value="wrong_correct">Wrong/Correct</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={severityFilter || 'all'}
            onValueChange={(v) => setSeverityFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
            </SelectContent>
          </Select>
          <Label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(!!checked)}
            />
            Show inactive
          </Label>
        </div>

        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : rules.length === 0 ? (
          <Empty>
            <EmptyIcon><FileText className="h-12 w-12" /></EmptyIcon>
            <EmptyTitle>No terminology rules</EmptyTitle>
            <EmptyDescription>
              Create rules to enforce Prosci terminology standards in Kaiya's responses.
            </EmptyDescription>
            <Button onClick={openCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Button>
          </Empty>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pattern</TableHead>
                    <TableHead>Replacement</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div>
                          <span className="font-mono text-sm">{rule.pattern}</span>
                          {rule.pattern_type !== 'exact' && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {rule.pattern_type}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {rule.replacement || rule.replacement_first_use || '-'}
                      </TableCell>
                      <TableCell>
                        {rule.category ? (
                          <Badge variant="outline">{rule.category}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={SEVERITY_VARIANTS[rule.severity] || 'secondary'}>
                          {rule.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(rule)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setRuleToDelete(rule)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </ContentContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={closeForm}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'New Terminology Rule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Rule Type</label>
                <Select
                  value={form.rule_type}
                  onValueChange={(v) => setForm({ ...form, rule_type: v })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wrong_correct">Wrong/Correct</SelectItem>
                    <SelectItem value="trademark">Trademark</SelectItem>
                    <SelectItem value="forbidden">Forbidden</SelectItem>
                    <SelectItem value="approved_term">Approved Term</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Severity</label>
                <Select
                  value={form.severity}
                  onValueChange={(v) => setForm({ ...form, severity: v })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="adkar, pct, roles..."
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Pattern</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm font-mono"
                  placeholder="change managment"
                  value={form.pattern}
                  onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pattern Type</label>
                <Select
                  value={form.pattern_type}
                  onValueChange={(v) => setForm({ ...form, pattern_type: v })}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">Exact</SelectItem>
                    <SelectItem value="regex">Regex</SelectItem>
                    <SelectItem value="word_boundary">Word Boundary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Replacement</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="change management"
                value={form.replacement}
                onChange={(e) => setForm({ ...form, replacement: e.target.value })}
              />
            </div>
            {form.rule_type === 'trademark' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">First Use Replacement</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    placeholder="Prosci(R)"
                    value={form.replacement_first_use}
                    onChange={(e) => setForm({ ...form, replacement_first_use: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Subsequent Replacement</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    placeholder="Prosci"
                    value={form.replacement_subsequent}
                    onChange={(e) => setForm({ ...form, replacement_subsequent: e.target.value })}
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Usage Notes</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                rows={2}
                value={form.usage_notes}
                onChange={(e) => setForm({ ...form, usage_notes: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.case_sensitive}
                  onCheckedChange={(checked) => setForm({ ...form, case_sensitive: !!checked })}
                />
                Case sensitive
              </Label>
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: !!checked })}
                />
                Active
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!ruleToDelete} onOpenChange={() => setRuleToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rule</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete the rule for "{ruleToDelete?.pattern}"?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => ruleToDelete && deleteMutation.mutate(ruleToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
